import {
  Activity,
  AlertTriangle,
  BookOpenText,
  BookUser,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  FileBarChart,
  FileStack,
  FolderKanban,
  Gauge,
  GraduationCap,
  History,
  Inbox,
  LayoutDashboard,
  Mail,
  Coins,
  Palette,
  Paperclip,
  Phone,
  Rocket,
  MailWarning,
  Scale,
  ScrollText,
  Send,
  Settings2,
  ShieldCheck,
  Users,
  UserCircle2,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import type { UserRole } from "@/lib/supabase";

type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavGroup {
  key: string;
  labelKey: string;
  items: readonly NavItem[];
}

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: string;
}

// Navigation par persona — l'utilisateur voit la combinaison des groupes
// correspondant aux rôles qu'il porte.
export const NAV_GROUPS: Record<UserRole, NavGroup> = {
  salarie: {
    key: "salarie",
    labelKey: "app.nav.group_employee",
    items: [
      { to: "/app", end: true, labelKey: "app.nav.parcours", icon: LayoutDashboard },
      { to: "/app/historique", labelKey: "app.nav.historique", icon: History },
      { to: "/app/rdv", labelKey: "app.nav.rdv", icon: CalendarClock },
      { to: "/app/formations", labelKey: "app.nav.formations", icon: GraduationCap },
      { to: "/app/messages", labelKey: "app.nav.messages", icon: Inbox },
      { to: "/app/bibliotheque", labelKey: "app.nav.bibliotheque", icon: BookOpenText },
    ],
  },
  drh: {
    key: "drh",
    labelKey: "app.nav.group_drh",
    items: [
      { to: "/app/drh", end: true, labelKey: "app.nav.dashboard", icon: LayoutDashboard },
      { to: "/app/drh/operations", labelKey: "app.nav.operations", icon: ClipboardList },
      { to: "/app/drh/salaries", labelKey: "app.nav.salaries", icon: Users },
      { to: "/app/drh/messages", labelKey: "app.nav.messages", icon: Inbox },
      { to: "/app/drh/relances", labelKey: "app.nav.relances", icon: Send },
      { to: "/app/drh/formations", labelKey: "app.nav.formations", icon: GraduationCap },
      { to: "/app/drh/reporting", labelKey: "app.nav.reporting", icon: Gauge },
      {
        to: "/app/drh/synthese",
        labelKey: "app.nav.synthese_mission",
        icon: FileBarChart,
      },
      { to: "/app/drh/bibliotheque", labelKey: "app.nav.bibliotheque", icon: BookOpenText },
    ],
  },
  consultant_aon: {
    key: "consultant_aon",
    labelKey: "app.nav.group_aon",
    items: [
      { to: "/app/aon", end: true, labelKey: "app.nav.dashboard", icon: LayoutDashboard },
      { to: "/app/aon/dossiers", labelKey: "app.nav.mes_dossiers", icon: FolderKanban },
      { to: "/app/aon/relances", labelKey: "app.nav.relances", icon: Phone },
      { to: "/app/aon/anomalies", labelKey: "app.nav.anomalies", icon: AlertTriangle },
      { to: "/app/aon/calendrier", labelKey: "app.nav.calendrier", icon: CalendarClock },
      { to: "/app/aon/messages", labelKey: "app.nav.messages", icon: Inbox },
      { to: "/app/aon/bibliotheque", labelKey: "app.nav.bibliotheque", icon: BookOpenText },
    ],
  },
  chef_projet_aon: {
    key: "chef_projet_aon",
    labelKey: "app.nav.group_aon",
    items: [
      { to: "/app/aon", end: true, labelKey: "app.nav.dashboard", icon: LayoutDashboard },
      { to: "/app/aon/operations", labelKey: "app.nav.operations", icon: ClipboardList },
      { to: "/app/aon/dossiers", labelKey: "app.nav.mes_dossiers", icon: FolderKanban },
      { to: "/app/aon/formations", labelKey: "app.nav.formations", icon: GraduationCap },
      { to: "/app/aon/revue", labelKey: "app.nav.revue", icon: ScrollText },
      { to: "/app/aon/anomalies", labelKey: "app.nav.anomalies", icon: AlertTriangle },
      { to: "/app/aon/equipe", labelKey: "app.nav.equipe", icon: Users },
      { to: "/app/aon/pilotage", labelKey: "app.nav.pilotage", icon: Gauge },
      {
        to: "/app/aon/synthese-drh",
        labelKey: "app.nav.synthese_mission",
        icon: FileBarChart,
      },
      { to: "/app/aon/messages", labelKey: "app.nav.messages", icon: Inbox },
      { to: "/app/aon/bibliotheque", labelKey: "app.nav.bibliotheque", icon: BookOpenText },
    ],
  },
  admin_aon: {
    key: "admin_aon",
    labelKey: "app.nav.group_admin",
    items: [
      { to: "/app/admin", end: true, labelKey: "app.nav.dashboard", icon: LayoutDashboard },
      { to: "/app/admin/onboarding", labelKey: "app.nav.onboarding", icon: Rocket },
      { to: "/app/admin/tenants", labelKey: "app.nav.tenants", icon: Building2 },
      { to: "/app/admin/operations", labelKey: "app.nav.operations", icon: ClipboardList },
      { to: "/app/admin/salaries", labelKey: "app.nav.salaries", icon: Users },
      { to: "/app/admin/utilisateurs", labelKey: "app.nav.utilisateurs", icon: UserCircle2 },
      { to: "/app/admin/formations", labelKey: "app.nav.formations", icon: GraduationCap },
      { to: "/app/admin/email-templates", labelKey: "app.nav.email_templates", icon: Mail },
      { to: "/app/admin/branding", labelKey: "app.nav.branding", icon: Palette },
      { to: "/app/admin/suivi-messages", labelKey: "app.nav.suivi_messages", icon: MailWarning },
      { to: "/app/admin/audit", labelKey: "app.nav.audit", icon: ScrollText },
      { to: "/app/admin/rachat-bareme", labelKey: "app.nav.rachat_bareme", icon: Coins },
      { to: "/app/admin/bibliotheque", labelKey: "app.nav.bibliotheque", icon: BookOpenText },
      { to: "/app/admin/annexes-bri", labelKey: "app.nav.annexes_bri", icon: Paperclip },
      { to: "/app/admin/rgpd", labelKey: "app.nav.rgpd", icon: ShieldCheck },
      {
        to: "/app/admin/controle-qualite",
        labelKey: "app.nav.controle_qualite",
        icon: ClipboardCheck,
      },
      { to: "/app/admin/types-documents", labelKey: "app.nav.document_types", icon: FileStack },
      { to: "/app/aon/anomalies", labelKey: "app.nav.anomalies", icon: AlertTriangle },
      { to: "/app/admin/referentiels", labelKey: "app.nav.referentiels", icon: CalendarClock },
      {
        to: "/app/admin/referentiel-professions",
        labelKey: "app.nav.referentiel_professions",
        icon: BookUser,
      },
      {
        to: "/app/admin/conventions-collectives",
        labelKey: "app.nav.conventions_collectives",
        icon: Scale,
      },
      { to: "/app/admin/couts-ia", labelKey: "app.nav.couts_ia", icon: Coins },
      { to: "/app/admin/stats-usage", labelKey: "app.nav.stats_usage", icon: Activity },
      { to: "/app/admin/services-externes", labelKey: "app.nav.external_services", icon: Activity },
      { to: "/app/admin/parametres", labelKey: "app.nav.parametres", icon: Settings2 },
    ],
  },
  // super_admin est cumulé avec admin_aon : pas de section propre, la
  // navigation admin s'applique telle quelle.
  super_admin: {
    key: "super_admin",
    labelKey: "app.nav.group_admin",
    items: [],
  },
};
